
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getPointsRedemptionHistory, PointsRedemptionData } from '@/services/databaseService';
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { format } from 'date-fns';

interface PointsRedemptionHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
}

const PointsRedemptionHistoryModal: React.FC<PointsRedemptionHistoryModalProps> = ({
  open,
  onOpenChange,
  userId,
}) => {
  const [redemptionHistory, setRedemptionHistory] = useState<PointsRedemptionData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    if (open && userId) {
      fetchRedemptionHistory();
    }
  }, [open, userId, currentPage]);

  const fetchRedemptionHistory = async () => {
    if (!userId) return;
    
    setIsLoading(true);
    try {
      const response = await getPointsRedemptionHistory(userId, currentPage, pageSize);
      
      if (response.success) {
        setRedemptionHistory(response.data);
        setTotalItems(response.count);
        setTotalPages(Math.ceil(response.count / pageSize));
      } else {
        console.error("Error fetching redemption history:", response.error);
      }
    } catch (error) {
      console.error("Exception fetching redemption history:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  // Helper function to format transaction reference
  const formatTransactionRef = (ref: any): string => {
    if (!ref) return "N/A";
    
    if (typeof ref === 'string') {
      // Check if it's a JSON string
      try {
        const parsed = JSON.parse(ref);
        if (parsed && typeof parsed === 'object') {
          // If it's a parsed object, format it nicely
          return parsed.ref_id ? String(parsed.ref_id) : String(parsed);
        }
        return String(parsed);
      } catch (e) {
        // If not a JSON string, return as is
        return ref;
      }
    }
    
    // If it's already an object
    if (typeof ref === 'object' && ref !== null) {
      return ref.ref_id ? String(ref.ref_id) : JSON.stringify(ref);
    }
    
    return String(ref);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Points Redemption History</DialogTitle>
          <DialogDescription>
            View your points redemption activity history.
          </DialogDescription>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex justify-center my-4">
            <div className="animate-spin h-6 w-6 border-2 border-primary rounded-full border-t-transparent"></div>
          </div>
        ) : redemptionHistory.length === 0 ? (
          <div className="text-center my-4">
            <p>No redemption history found.</p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {redemptionHistory.map((item) => (
                <div key={item.id} className="p-3 border rounded-md">
                  <div className="flex justify-between">
                    <span className="font-medium">{item.points} points</span>
                    <span className="text-sm text-gray-500">
                      {format(new Date(item.created_at), 'MMM d, yyyy')}
                    </span>
                  </div>
                  {item.transaction_amt && (
                    <div className="text-sm">
                      <span>Transaction amount: ${item.transaction_amt}</span>
                    </div>
                  )}
                  {item.transaction_ref && (
                    <div className="text-sm">
                      <span>Reference: {formatTransactionRef(item.transaction_ref)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination controls */}
            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-gray-500">
                Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalItems)} of {totalItems}
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PointsRedemptionHistoryModal;
